import socketIOClient, { Socket } from "socket.io-client"

import { FeatureFlag } from "@common-shared/config/featureFlags.config"
import { SocketEvent } from "@common-shared/config/sockets.config"
import {
    IOneVerifyEhrWriteStatusUpdatedSocketsPayload,
    IToastMsgSocketsPayload,
} from "@common-shared/types/object.types"
import { IOneVerifyPolicyComment } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { FRONT_SOCKETS_CONFIG } from "@front-shared/config/sockets.config"
import { ToastrHelper } from "@front-shared/helpers/toastr.helper"

import { GENERAL_CONFIG } from "../config/general.config"
import { checkerUpdatedResultEhrWriteStatus } from "../store/slices/checker.slice"
import { policiesUpdatedSelectedItemEhrWriteStatus } from "../store/slices/policies.slice"
import { type AppDispatch } from "../store/store"
import { policiesNewCommentReceivedThroughSockets } from "../store/thunks/policyComments.thunks"
import { AuthHelper } from "./auth.helper"
import { FeatureFlagsHelper } from "./featureFlags.helper"

let instance: SocketsHelper

export class SocketsHelper {
    io: Socket = {} as Socket

    // Right now we are not connecting therapists to any additional rooms
    // Just to default that are handled during connection in API gateway
    // But in the future we might want to add them to some other rooms
    // So implemented this just in case
    additionalSubscribedRooms: { room: string; data: any }[] = []

    reconnectionAttempts = 0

    /**
     * Get singleton instance of helper
     */
    static getInstance(): SocketsHelper {
        if (!instance) {
            instance = new SocketsHelper()
            instance.io = socketIOClient(
                `${import.meta.env.VITE_SOCKETS_URL}`,
                FRONT_SOCKETS_CONFIG.defaultSocketIOConfig,
            )
        }

        return instance
    }

    /**
     * Check if sockets are connected right now
     */
    isConnected(): boolean {
        if (!this.io || !this.io.connect) {
            return false
        }

        return this.io.connected
    }

    /**
     * Reconnect sockets
     */
    async reconnect(dispatch: AppDispatch, enabledFeatureFlags: FeatureFlag[]) {
        if (!this.io || !this.io.connect) {
            return
        }

        this.disconnect()

        this.io.io.opts.query = {}

        // Add jwt token if it exists to our connection query
        // So we can identify user
        const jwtToken = await AuthHelper.getAccessToken()

        if (jwtToken) {
            this.io.io.opts.query.jwtToken = jwtToken
        }

        this.io.connect()

        this.io.on("connect", () => {
            // console.log("Successfully connected!")

            this.reconnectionAttempts = 0
            this.setEventsProcessors(dispatch, enabledFeatureFlags)

            // Rejoin needed rooms (that were previously connected)
            for (const roomConfig of this.additionalSubscribedRooms) {
                this.subscribeToRoom(roomConfig.room, roomConfig.data)
            }
        })

        this.io.on("connect_error", () => {
            this.reconnectionAttempts++
        })
    }

    /**
     * Disconnect sockets
     */
    disconnect(clearAdditionalRooms = false) {
        try {
            if (!this.io || !this.io.connect) {
                return
            }

            this.io.disconnect()
            this.reconnectionAttempts = 0

            if (clearAdditionalRooms) {
                this.additionalSubscribedRooms = []
            }
        } catch (e) {
            // console.log(e)
        }
    }

    /**
     * Subscribe somebody to room
     */
    private subscribeToRoom(room: string, data: any = {}) {
        this.io.emit(SocketEvent.oneVerifyJoinRoom, { room, data })

        // Check if such room already exists in our array, if not push it there for future use
        const existing = this.additionalSubscribedRooms.find(
            item =>
                item.room === room &&
                JSON.stringify(item.data) === JSON.stringify(data),
        )

        if (existing) {
            return
        }

        this.additionalSubscribedRooms.push({
            room,
            data,
        })
    }

    /**
     * Unsubscribe somebody from room
     */
    // @ts-ignore
    private unsubscribeFromRoom(room: string, data: any = {}) {
        this.io.emit(SocketEvent.oneVerifyLeaveRoom, room, data)

        this.additionalSubscribedRooms = this.additionalSubscribedRooms.filter(
            item =>
                !(
                    item.room === room &&
                    JSON.stringify(item.data) === JSON.stringify(data)
                ),
        )
    }

    /**
     * Set event processors for socket io
     */
    private setEventsProcessors(
        dispatch: AppDispatch,
        enabledFeatureFlags: FeatureFlag[],
    ) {
        const hasEhrWriteSingleFlag = FeatureFlagsHelper.isFeatureFlagEnabled(
            enabledFeatureFlags,
            FeatureFlag.ONE_VERIFY_EHR_WRITE_SINGLE,
        )

        if (
            hasEhrWriteSingleFlag &&
            !this.io.hasListeners(SocketEvent.oneVerifyEhrWriteStatusUpdated)
        ) {
            this.io.on(
                SocketEvent.oneVerifyEhrWriteStatusUpdated,
                (payload: IOneVerifyEhrWriteStatusUpdatedSocketsPayload) => {
                    dispatch(policiesUpdatedSelectedItemEhrWriteStatus(payload))

                    dispatch(checkerUpdatedResultEhrWriteStatus(payload))
                },
            )
        }

        // SINGLE TOAST MESSAGE FROM BACKGROUND
        if (!this.io.hasListeners(SocketEvent.oneVerifySingleToastMsg)) {
            this.io.on(
                SocketEvent.oneVerifySingleToastMsg,
                (payload: IToastMsgSocketsPayload) => {
                    let toastHandler:
                        | ((
                              msg: string,
                              timeOut?: number,
                              withHtml?: boolean,
                          ) => void)
                        | undefined = undefined

                    switch (payload.type) {
                        case "success":
                            toastHandler = ToastrHelper.success
                            break

                        case "warning":
                            toastHandler = ToastrHelper.warning
                            break

                        case "error":
                            toastHandler = ToastrHelper.error
                            break
                    }

                    if (!toastHandler) {
                        return
                    }

                    toastHandler(
                        payload.msg,
                        // Extended by default since user isn't expecting these error messages in interface
                        payload.timeOut || GENERAL_CONFIG.extendedToastrTimeout,
                        payload.withHtml,
                    )
                },
            )
        }

        // NEW COMMENT THROUGH SOCKETS
        if (!this.io.hasListeners(SocketEvent.oneVerifyPolicyCommentCreated)) {
            this.io.on(
                SocketEvent.oneVerifyPolicyCommentCreated,
                (payload: IOneVerifyPolicyComment) => {
                    dispatch(
                        policiesNewCommentReceivedThroughSockets({
                            payload,
                        }),
                    )
                },
            )
        }
    }
}
